import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, CircularProgress, Alert, Box, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  showReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  maxReasonLength?: number;
  onConfirm: (reason?: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
  confirmColor?: 'primary' | 'error' | 'warning' | 'info' | 'success';
}
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  showReason = false,
  reasonLabel = 'Reason (optional)',
  reasonPlaceholder = 'Enter reason...',
  maxReasonLength = 200,
  onConfirm,
  onCancel,
  isLoading = false,
  error,
  confirmColor = 'primary'
}) => {
  const [reason, setReason] = useState('');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const handleConfirm = () => {
    onConfirm(showReason ? reason : undefined);
  };
  return <Dialog open={open} onClose={isLoading ? undefined : onCancel} maxWidth="sm" fullWidth fullScreen={fullScreen} PaperProps={{
    sx: {
      borderRadius: fullScreen ? 0 : 3,
      overflow: 'hidden'
    }
  }} aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-description" data-id="element-1224">
      {fullScreen && <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      p: 1,
      borderBottom: '1px solid',
      borderColor: 'divider'
    }} data-id="element-1225">
          <IconButton edge="end" color="inherit" onClick={onCancel} aria-label="close" disabled={isLoading} data-id="element-1226">
            <Icon icon="lucide:x" width={24} height={24} data-id="element-1227" />
          </IconButton>
        </Box>}
      <DialogTitle sx={{
      fontWeight: 700,
      color: '#1F2937'
    }} data-id="element-1228">
        {title}
      </DialogTitle>
      <DialogContent data-id="element-1229">
        <DialogContentText id="confirm-dialog-description" sx={{
        mb: 2
      }} data-id="element-1230">
          {message}
        </DialogContentText>
        {error && <Alert severity="error" sx={{
        mb: 2,
        borderRadius: 2
      }} variant="outlined" data-id="element-1231">
            {error}
          </Alert>}
        {showReason && <TextField autoFocus label={reasonLabel} placeholder={reasonPlaceholder} fullWidth multiline rows={3} value={reason} onChange={e => setReason(e.target.value)} inputProps={{
        maxLength: maxReasonLength
      }} helperText={`${reason.length}/${maxReasonLength}`} disabled={isLoading} sx={{
        mt: 2
      }} data-id="element-1232" />}
      </DialogContent>
      <DialogActions sx={{
      p: 2
    }} data-id="element-1233">
        <Button onClick={onCancel} color="inherit" data-id="element-1234">
          {cancelLabel}
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={isLoading} sx={{
        backgroundColor: confirmColor === 'error' ? '#AE2A19' : '#0052CC',
        '&:hover': {
          backgroundColor: confirmColor === 'error' ? '#8B1D0F' : '#003DA8'
        }
      }} data-id="element-1235">
          {isLoading ? 'Processing...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>;
};
export default ConfirmDialog;